import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
// imports only needed inside LandingHero
import soccerData from '../../data/cities/soccerCities.json';
import soccerDefaults from '../../data/sports/soccerDefaults.json';
import basketballData from '../../data/cities/basketballCities.json';
import basketballDefaults from '../../data/sports/basketballDefaults.json';
import danceData from '../../data/cities/danceCities.json';
import baseballData from '../../data/cities/baseballCities.json';
import baseballDefaults from '../../data/sports/baseballDefaults.json';
import volleyballData from '../../data/cities/volleyballCities.json';
import martialArtsData from '../../data/cities/martialArtsCities.json';
import tennisData from '../../data/cities/tennisCities.json';
import golfData from '../../data/cities/golfCities.json';
import runningData from '../../data/cities/runningCities.json';
import yogaData from '../../data/cities/yogaCities.json';
import { KiddiFitWordmark } from '../Brand';
import Button from '../Button';
import './GeoSportPage.css';
import '../LandingPage.css';
// hero behavior encapsulated in LandingHero
import Panel from '../landing/Panel';
import { FeaturesSection, FeatureCard } from '../landing/FeaturesGrid';
import StateGuidesList from '../StateGuidesList';
import basketballDrills from '../../data/drills/basketball-drills.js';
import soccerDrills from '../../data/drills/soccer-drills.js';
import baseballDrills from '../../data/drills/baseball-drills.js';
// Import sport-specific background images
import basketballBg from '../../assets/backgrounds/basketball.jpg';
import baseballBg from '../../assets/backgrounds/baseball.jpg';
import soccerBg from '../../assets/backgrounds/soccer-background.jpg';

// Function to get sport-specific background image
const getSportBackground = (sport) => {
  switch (sport) {
    case 'basketball':
      return basketballBg;
    case 'baseball':
      return baseballBg;
    case 'soccer':
      return soccerBg;
    case 'volleyball':
    case 'tennis':
    case 'golf':
    case 'martial-arts':
    case 'dance':
    case 'running':
    case 'yoga':
    default:
      return soccerBg; // fallback to soccer background for other sports
  }
};

function getCityRecord(sport, slug) {
  const SPORT_CITY_DATA = {
    soccer: soccerData,
    basketball: basketballData,
    dance: danceData,
    baseball: baseballData,
    volleyball: volleyballData,
    'martial-arts': martialArtsData,
    tennis: tennisData,
    golf: golfData,
    running: runningData,
    yoga: yogaData,
  };
  const data = SPORT_CITY_DATA[sport] || [];
  const city = (data || []).find((c) => c.slug === slug) || null;
  if (!city) return null;
  // Merge sport defaults with city
  let merged = { ...city };
  if (sport === 'soccer' && soccerDefaults) {
    merged = deepMergeDefaults(soccerDefaults, city);
  } else if (sport === 'basketball' && basketballDefaults) {
    merged = deepMergeDefaults(basketballDefaults, city);
  } else if (sport === 'baseball' && baseballDefaults) {
    merged = deepMergeDefaults(baseballDefaults, city);
  }
  return merged;
}

function deepMergeDefaults(defaults, city) {
  // Shallow copy base
  const out = { ...defaults, ...city };
  // Objects: parentResources
  if (defaults.parentResources || city.parentResources) {
    out.parentResources = {
      ...(defaults.parentResources || {}),
      ...(city.parentResources || {}),
    };
    // Nested: nutrition/equipmentGuide/injuryPrevention/costs/scholarships merge
    const pr = out.parentResources;

    // Costs: use defaults unless city overrides
    if (defaults.parentResources?.costs || city.parentResources?.costs) {
      pr.costs = {
        ...(defaults.parentResources?.costs || {}),
        ...(city.parentResources?.costs || {}),
      };
    }

    // Scholarships: use defaults unless city overrides
    if (
      defaults.parentResources?.scholarships ||
      city.parentResources?.scholarships
    ) {
      pr.scholarships =
        city.parentResources?.scholarships ||
        defaults.parentResources?.scholarships ||
        [];
    }

    if (
      defaults.parentResources?.nutrition ||
      city.parentResources?.nutrition
    ) {
      pr.nutrition = {
        ...(defaults.parentResources?.nutrition || {}),
        ...(city.parentResources?.nutrition || {}),
      };
    }
    if (
      defaults.parentResources?.equipmentGuide ||
      city.parentResources?.equipmentGuide
    ) {
      pr.equipmentGuide = {
        ...(defaults.parentResources?.equipmentGuide || {}),
        ...(city.parentResources?.equipmentGuide || {}),
      };
      if (
        defaults.parentResources?.equipmentGuide?.ballSize ||
        city.parentResources?.equipmentGuide?.ballSize
      ) {
        pr.equipmentGuide.ballSize = {
          ...(defaults.parentResources?.equipmentGuide?.ballSize || {}),
          ...(city.parentResources?.equipmentGuide?.ballSize || {}),
        };
      }
      if (
        !Array.isArray(pr.equipmentGuide.checklist) &&
        Array.isArray(defaults.parentResources?.equipmentGuide?.checklist)
      ) {
        pr.equipmentGuide.checklist =
          defaults.parentResources.equipmentGuide.checklist;
      }
    }
    if (
      defaults.parentResources?.injuryPrevention ||
      city.parentResources?.injuryPrevention
    ) {
      pr.injuryPrevention = {
        ...(defaults.parentResources?.injuryPrevention || {}),
        ...(city.parentResources?.injuryPrevention || {}),
      };
    }
  }
  // Arrays: faq, safety → combine defaults + city (city can set replace flags)
  const replaceFaq = city.replaceFaq === true;
  const replaceSafety = city.replaceSafety === true;
  if (Array.isArray(defaults.faq) || Array.isArray(city.faq)) {
    out.faq = replaceFaq
      ? city.faq || []
      : [...(defaults.faq || []), ...(city.faq || [])];
  }
  if (Array.isArray(defaults.safety) || Array.isArray(city.safety)) {
    out.safety = replaceSafety
      ? city.safety || []
      : [...(defaults.safety || []), ...(city.safety || [])];
  }
  return out;
}

function buildTitle(sport, city, state) {
  const sportName = sport.charAt(0).toUpperCase() + sport.slice(1);
  return `Youth ${sportName} in ${city}, ${state} — Leagues, Drills, 10‑Minute Plan`;
}

function getDrillEmoji(name = '') {
  const key = name.toLowerCase();
  if (key.includes('toe') && key.includes('tap')) return '👣';
  if (key.includes('foundation')) return '👟';
  if (key.includes('drag') && key.includes('back')) return '🔙';
  if (key.includes('control')) return '🎯';
  if (key.includes('turn')) return '↩️';
  if (key.includes('speed')) return '⚡';
  if (key.includes('ladder')) return '🪜';
  if (key.includes('dribbling')) return '🏀';
  if (key.includes('shooting')) return '🎯';
  if (key.includes('defense')) return '🛡️';
  if (key.includes('baseball')) return '⚾';
  if (key.includes('soccer')) return '⚽';
  return '⚽';
}

function getDefaultDrills(sport) {
  const drillMap = {
    basketball: basketballDrills,
    soccer: soccerDrills,
    baseball: baseballDrills,
  };

  const drills = drillMap[sport] || [];
  // Return first 3 beginner drills
  return drills
    .filter((drill) => drill.difficulty === 'beginner')
    .slice(0, 3)
    .map((drill) => ({
      id: drill.id,
      name: drill.name,
      benefit: `Improves ${drill.skill.toLowerCase()} skills`,
      steps:
        drill.timedInstructions
          ?.slice(0, 3)
          .map((instruction) => instruction.text) || [],
    }));
}

export default function GeoSportPage({ sport = 'soccer', slug }) {
  const city = useMemo(() => getCityRecord(sport, slug), [sport, slug]);

  // Scroll to top on component mount and when slug changes
  // Removed useEffect to prevent hydration issues

  // no local hero behavior needed
  // const { isSamplePlaying, handleSampleWorkout } = useSampleWorkoutAudio([
  //   '/latest-generated-workout-audio/soccer_session_1_intro.mp3',
  //   '/latest-generated-exercises-audio/fastFeet_intro.mp3',
  //   '/latest-generated-exercises-audio/fastFeet.mp3',
  //   '/latest-generated-exercises-audio/highKnees_intro.mp3',
  //   '/latest-generated-exercises-audio/highKnees.mp3',
  //   '/latest-generated-exercises-audio/jumpingJacks_intro.mp3',
  //   '/latest-generated-exercises-audio/jumpingJacks.mp3',
  //   '/latest-generated-workout-audio/soccer_session_1_conclusion.mp3',
  // ]);
  if (!city)
    return (
      <div className='geo-page'>
        <div className='geo-container'>City not found.</div>
      </div>
    );

  const title = buildTitle(sport, city.city, city.state);
  const desc =
    city.intro ||
    `Helpful youth ${sport} guide for ${city.city}, ${city.state}: leagues, at‑home drills, and a simple practice plan.`;
  const url = `https://kiddifit.com/${city.slug}`;

  return (
    <div className='geo-page'>
      <Helmet>
        <title>{title}</title>
        <meta name='description' content={desc} />
        <meta name='keywords' content={(city.seo_keywords || []).join(', ')} />
        <link rel='canonical' href={url} />
        <meta property='og:title' content={title} />
        <meta property='og:description' content={desc} />
        <meta property='og:url' content={url} />
        <meta property='og:type' content='article' />
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: title,
            description: desc,
            url,
          })}
        </script>
        {Array.isArray(city.faq) && city.faq.length ? (
          <script type='application/ld+json'>
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: city.faq.map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            })}
          </script>
        ) : null}
      </Helmet>

      <section className='hero-section'>
        <div
          className='geo-hero-bg geo-hero-bg-dynamic'
          style={{
            backgroundImage: `url(${getSportBackground(sport)})`,
          }}
        />
        <div className='hero-content'>
          <h2>
            <a href='/' className='footer-link-clean'>
              <KiddiFitWordmark /> <span className='guides-badge'>Guides</span>
            </a>
          </h2>
          <h1>
            Youth {sport} in {city.city}, {city.state}
          </h1>
          <p className='hero-description'>{city.intro}</p>
        </div>
      </section>

      <FeaturesSection heading={`Leagues in ${city.city}, ${city.state}`}>
        {city.leagues?.length ? (
          city.leagues.map((l, i) => (
            <FeatureCard
              key={i}
              title={l.name}
              footer={
                l.url ? (
                  <div className='button-wrapper'>
                    <a
                      href={l.url}
                      target='_blank'
                      rel='noreferrer'
                      className='button button-primary button-medium'
                    >
                      Website
                    </a>
                  </div>
                ) : null
              }
            >
              <p>{l.blurb || ''}</p>
              <p>{l.ages ? `\nAges: ${l.ages}` : ''}</p>
              <p>{l.seasons ? `\nSeasons: ${l.seasons}` : ''}</p>
            </FeatureCard>
          ))
        ) : (
          <p className='full-width-grid-item'>
            Recreational seasons: {city.seasons?.rec || 'Fall & Spring'}; club:{' '}
            {city.seasons?.club || 'Year‑round'}.
          </p>
        )}
      </FeaturesSection>

      {city.clubs?.length ? (
        <FeaturesSection heading={`Clubs in ${city.city}, ${city.state}`}>
          {city.clubs.map((c, i) => (
            <FeatureCard
              key={i}
              title={c.name}
              footer={
                c.url ? (
                  <div className='button-wrapper'>
                    <a
                      href={c.url}
                      target='_blank'
                      rel='noreferrer'
                      className='button button-primary button-medium'
                    >
                      Website
                    </a>
                  </div>
                ) : null
              }
            >
              <p>{c.blurb || ''}</p>
              <p>{c.ages ? `\nAges: ${c.ages}` : ''}</p>
              <p>
                {Array.isArray(c.leagues) && c.leagues.length
                  ? `\nLeagues: ${c.leagues.join(', ')}`
                  : ''}
              </p>
              <p>
                {Array.isArray(c.fields) && c.fields.length
                  ? `\nFields: ${c.fields.join(', ')}`
                  : ''}
              </p>
            </FeatureCard>
          ))}
        </FeaturesSection>
      ) : null}

      {/* Fields & Facilities */}
      {Array.isArray(city.fields) && city.fields.length ? (
        <Panel title={`Fields & Facilities in ${city.city}, ${city.state}`}>
          <table className='panel-table fields'>
            <thead>
              <tr>
                <th scope='col'>Name</th>
                <th scope='col'>Address</th>
                <th scope='col'>Amenities</th>
                <th scope='col'>Notes</th>
              </tr>
            </thead>
            <tbody>
              {city.fields.map((f, i) => (
                <tr key={i}>
                  <td>{f.name}</td>
                  <td>{f.address}</td>
                  <td>
                    {Array.isArray(f.amenities) ? f.amenities.join(', ') : ''}
                  </td>
                  <td>{f.blurb || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      <Panel title={'Age Guides'}>
        {Object.keys(city.ages || {}).length ? (
          <table className='panel-table ages'>
            <tbody>
              {Object.entries(city.ages).map(([k, v]) => (
                <tr key={k}>
                  <th scope='row'>{k}</th>
                  <td>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p>
            Keep it playful for younger ages; add speed and combos for older
            kids.
          </p>
        )}
      </Panel>

      {/* Parent Resources: Costs */}
      {city.parentResources?.costs ? (
        <Panel title={'Typical Costs'}>
          <table className='panel-table costs'>
            <tbody>
              <tr>
                <th scope='row'>Rec</th>
                <td>{city.parentResources.costs.rec}</td>
              </tr>
              <tr>
                <th scope='row'>Club</th>
                <td>{city.parentResources.costs.club}</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      ) : null}

      {/* Parent Resources: Scholarships */}
      {Array.isArray(city.parentResources?.scholarships) &&
      city.parentResources.scholarships.length ? (
        <Panel
          title={`Scholarships & Fee Assistance in ${city.city}, ${city.state}`}
        >
          <table className='panel-table scholarships'>
            <thead>
              <tr>
                <th scope='col'>Program</th>
                <th scope='col'>Details</th>
              </tr>
            </thead>
            <tbody>
              {city.parentResources.scholarships.map((s, i) => (
                <tr key={i}>
                  <td>
                    {s.url ? (
                      <a href={s.url} target='_blank' rel='noreferrer'>
                        {s.name}
                      </a>
                    ) : (
                      s.name
                    )}
                  </td>
                  <td>{s.blurb}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      {/* Parent Resources: Injury Prevention */}
      {city.parentResources?.injuryPrevention ? (
        <Panel title={'Injury Prevention'}>
          <table className='panel-table injuries'>
            <tbody>
              <tr>
                <th scope='row'>Common Injuries</th>
                <td>
                  {(
                    city.parentResources.injuryPrevention.commonInjuries || []
                  ).join(', ')}
                </td>
              </tr>
              <tr>
                <th scope='row'>Prevention Tips</th>
                <td>
                  {(
                    city.parentResources.injuryPrevention.preventionTips || []
                  ).join(' • ')}
                </td>
              </tr>
              <tr>
                <th scope='row'>Concussion Protocol</th>
                <td>
                  {city.parentResources.injuryPrevention.concussionProtocol ||
                    ''}
                </td>
              </tr>
            </tbody>
          </table>
        </Panel>
      ) : null}

      {/* Parent Resources: Nutrition */}
      {city.parentResources?.nutrition ? (
        <Panel title={'Nutrition Basics'}>
          <table className='panel-table nutrition'>
            <tbody>
              <tr>
                <th scope='row'>Pre‑Game</th>
                <td>{city.parentResources.nutrition.preGame}</td>
              </tr>
              <tr>
                <th scope='row'>During</th>
                <td>{city.parentResources.nutrition.duringGame}</td>
              </tr>
              <tr>
                <th scope='row'>Post‑Game</th>
                <td>{city.parentResources.nutrition.postGame}</td>
              </tr>
            </tbody>
          </table>
        </Panel>
      ) : null}

      {/* Parent Resources: Equipment Guide */}
      {city.parentResources?.equipmentGuide ? (
        <Panel title={'Equipment Guide'}>
          <table className='panel-table'>
            <tbody>
              <tr>
                <th scope='row'>Ball Size</th>
                <td>
                  {city.parentResources.equipmentGuide.ballSize?.['U6-U8']
                    ? `U6‑U8: ${city.parentResources.equipmentGuide.ballSize['U6-U8']}`
                    : ''}
                  {city.parentResources.equipmentGuide.ballSize?.['U9-U12']
                    ? `, U9‑U12: ${city.parentResources.equipmentGuide.ballSize['U9-U12']}`
                    : ''}
                  {city.parentResources.equipmentGuide.ballSize?.['U13+']
                    ? `, U13+: ${city.parentResources.equipmentGuide.ballSize['U13+']}`
                    : ''}
                </td>
              </tr>
              <tr>
                <th scope='row'>Checklist</th>
                <td>
                  {(city.parentResources.equipmentGuide.checklist || []).join(
                    ' • '
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </Panel>
      ) : null}

      {/* Parent Resources: Camps & Clinics */}
      {Array.isArray(city.parentResources?.campsAndClinics) &&
      city.parentResources.campsAndClinics.length ? (
        <Panel title={`Camps & Clinics in ${city.city}, ${city.state}`}>
          <table className='panel-table camps'>
            <thead>
              <tr>
                <th scope='col'>Provider</th>
                <th scope='col'>Details</th>
              </tr>
            </thead>
            <tbody>
              {city.parentResources.campsAndClinics.map((c, i) => (
                <tr key={i}>
                  <td>
                    {c.url ? (
                      <a href={c.url} target='_blank' rel='noreferrer'>
                        {c.name}
                      </a>
                    ) : (
                      c.name
                    )}
                  </td>
                  <td>{c.blurb}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      {/* Parent Resources: Local Tournaments */}
      {Array.isArray(city.parentResources?.localTournaments) &&
      city.parentResources.localTournaments.length ? (
        <Panel title={`Local Tournaments in ${city.city}, ${city.state}`}>
          <table className='panel-table tournaments'>
            <tbody>
              {city.parentResources.localTournaments.map((t, i) => (
                <tr key={i}>
                  <th scope='row'>{t.name}</th>
                  <td>{t.blurb}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      {city.safety?.length ? (
        <Panel title={'Safety & Space Tips'}>
          <table className='panel-table safety'>
            <tbody>
              {city.safety.map((s, i) => (
                <tr key={i}>
                  <td>{s}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      <Panel
        title={'At‑Home 10‑Minute Plan'}
        subtitle={'A simple weekly rhythm you can actually stick to'}
      >
        {city.weeklyPlan?.length ? (
          <div className='coaching-features'>
            {city.weeklyPlan.map((d, i) => (
              <div className='coaching-feature' key={i}>
                <div className='coaching-icon'>{d.day}</div>
                <div className='coaching-text'>
                  <h4>
                    {d.title} • {d.durationMin} min
                  </h4>
                  <p>
                    {d.focus}
                    {d.drills?.length ? ` — ${d.drills.join(', ')}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className='sample-subtitle'>
            Short, no‑equipment sessions 3–5x/week keep kids confident between
            team practices.
          </p>
        )}
      </Panel>

      <section className='geo-section cta cta-section-extra-padding'>
        <div className='geo-container'>
          <div className='cta-panel'>
            <div>
              <h3>Try a free audio {sport} session!</h3>
              <p>
                Build confidence in 10 minutes a day with <KiddiFitWordmark />.
                No equipment needed.
              </p>
            </div>
            <a
              href='/'
              className='button button-primary'
              data-analytics-id='geo_cta_try_free'
            >
              Get started
            </a>
          </div>
        </div>
      </section>

      <section className='testimonials-section'>
        <div className='container'>
          <h2>
            Top Kid‑Friendly {sport.charAt(0).toUpperCase() + sport.slice(1)}{' '}
            Drills
          </h2>
          {city.drills?.length || getDefaultDrills(sport).length ? (
            <div className='testimonials-grid'>
              {(city.drills || getDefaultDrills(sport)).map((dr) => (
                <div className='testimonial-card' key={dr.id}>
                  <div className='testimonial-content'>
                    <p>
                      <strong>{dr.name}</strong>
                    </p>
                    <p className='drill-meta-line'>{dr.benefit}</p>
                  </div>
                  {dr.steps?.length ? (
                    <div className='testimonial-content'>
                      <p>{dr.steps.join(' • ')}</p>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p>
              Simple ball mastery and turns fit in any driveway or small space.
            </p>
          )}
        </div>
      </section>

      {city.faq?.length ? (
        <Panel title={'FAQ'}>
          <table className='panel-table faq'>
            <tbody>
              {city.faq.map((f, i) => (
                <tr key={i}>
                  <td>
                    <p className='faq-question'>{f.q}</p>
                    <p className='faq-answer'>{f.a}</p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      ) : null}

      <section className='geo-section cta'>
        <div className='geo-container'>
          <div className='cta-panel'>
            <div>
              <h3>Try a free audio {sport} session!</h3>
              <p>
                Build confidence in 10 minutes a day with <KiddiFitWordmark />.
                No equipment needed.
              </p>
            </div>
            <a
              href='/'
              className='button button-primary'
              data-analytics-id='geo_cta_try_free'
            >
              Get started
            </a>
          </div>
        </div>
        <br />
        <br />
      </section>

      {/* State Guides Section */}
      <StateGuidesList />

      {/* Footer */}
      <footer className='landing-footer'>
        <div className='container'>
          <p>
            &copy; 2025 HHG Labs,{' '}
            <a href='/' className='footer-link-clean'>
              <KiddiFitWordmark />
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
